<template>
    <div class="column">
        <div class="flex-column-with-gap">
            <input-with-label label="Mode">
                <select-button
                    :model-value="activeDatePickerTab"
                    :options="datePickerTabOptions"
                    option-label="name"
                    option-value="id"
                    option-disabled="disabled"
                    :allow-empty="false"
                    :disabled="props.dateSelectionLocked"
                    @update:model-value="updateActiveDatePickerTab"
                >
                    <template #option="{ option }">
                        <span>{{ option.name }}</span>
                        <info-icon-with-tooltip v-if="option.info" :tooltip-text="option.info" />
                    </template>
                </select-button>
            </input-with-label>
        </div>
        <div class="separator-wrapper">
            <div class="section-title">Options</div>
            <div class="separator" />
        </div>
        <primevue-select
            v-if="activeDatePickerTab === 'preset'"
            class="quick-range-select"
            :model-value="quickSelectDateRangeId"
            :options="quickRangeSelectOptions"
            option-label="name"
            option-value="id"
            :disabled="props.dateSelectionLocked"
            scroll-height="300px!important"
            @update:model-value="selectQuickRange"
        />
        <div v-if="activeDatePickerTab === 'dates'" class="flex-column-with-gap">
            <input-with-label v-if="!props.onlyShowSingleDay" label="Selection">
                <select-button
                    :model-value="dateMode"
                    :options="fixedDateModeOptions"
                    option-label="name"
                    option-value="id"
                    :allow-empty="false"
                    :disabled="props.dateSelectionLocked"
                    @update:model-value="updateFixedDateMode"
                />
            </input-with-label>
            <div class="specific-date-row">
                <input-with-label :label="showFixedDateRange ? 'Start' : fixedDateLabel" class="specific-date-picker">
                    <basic-date-picker
                        :model-value="selectedFromDate"
                        selection-mode="single"
                        :format="fixedDatePickerFormat"
                        :view="fixedDatePickerView"
                        :disabled="props.dateSelectionLocked"
                        :min-date="props.minDate"
                        :max-date="props.maxDate"
                        full-width
                        @update:model-value="updateSpecificDateSelection($event, 'from')"
                    />
                </input-with-label>
                <input-with-label v-if="showFixedDateRange" label="End" class="specific-date-picker">
                    <basic-date-picker
                        :model-value="selectedToDate"
                        selection-mode="single"
                        :format="fixedDatePickerFormat"
                        :view="fixedDatePickerView"
                        :disabled="props.dateSelectionLocked"
                        :min-date="props.minDate"
                        :max-date="props.maxDate"
                        full-width
                        @update:model-value="updateSpecificDateSelection($event, 'to')"
                    />
                </input-with-label>
            </div>
        </div>
        <div v-if="activeDatePickerTab === 'relative'" class="rolling-period-panel">
            <relative-date-range-slider
                v-model="rollingOffsetRange"
                :default-value="defaultRollingOffsetRange"
                :min="rollingSliderMin"
                :max="rollingSliderMax"
                :disabled="props.dateSelectionLocked"
                custom-reset-button-text="Reset to current selection"
            />
            <div class="rolling-input-grid">
                <div class="rolling-input">
                    <label for="rollingStartOffset">Start</label>
                    <input-number
                        class="signed-day-input"
                        v-model="rollingStartSignedOffset"
                        show-buttons
                        :min="rollingInputMin"
                        :max="rollingInputMax"
                        :disabled="props.dateSelectionLocked"
                        input-id="rollingStartOffset"
                    />
                    <span>days</span>
                </div>
                <div class="rolling-input">
                    <label for="rollingEndOffset">End</label>
                    <input-number
                        class="signed-day-input"
                        v-model="rollingEndSignedOffset"
                        show-buttons
                        :min="rollingInputMin"
                        :max="rollingInputMax"
                        :disabled="props.dateSelectionLocked"
                        input-id="rollingEndOffset"
                    />
                    <span>days</span>
                </div>
            </div>
        </div>
        <accordion v-if="showHourRangeOptions" class="advanced-time-panel" v-model:value="advancedTimePanelValue">
            <accordion-panel value="advanced-time">
                <accordion-header>Advanced</accordion-header>
                <accordion-content>
                    <div class="flex-column-with-gap">
                        <input-with-label label="Time">
                            <select-button
                                v-model="hourRangeMode"
                                :options="hourRangeModeOptions"
                                option-label="name"
                                option-value="id"
                                option-disabled="disabled"
                                :allow-empty="false"
                                :disabled="props.dateSelectionLocked"
                            >
                                <template #option="slotProps">
                                    {{ slotProps.option.name }}
                                    <info-icon-with-tooltip
                                        v-if="slotProps.option.tooltip"
                                        :tooltip-text="slotProps.option.tooltip"
                                    />
                                </template>
                            </select-button>
                        </input-with-label>
                        <div v-if="hourRangeMode === 'fixed'" class="time-input-row">
                            <div class="flex-row-with-gap">
                                <label>Start</label>
                                <input-number class="time-input-number-width" v-model="startHour" show-buttons :min="0" :max="23" :disabled="props.dateSelectionLocked" />
                                <span>:</span>
                                <input-number class="time-input-number-width" v-model="startMinute" show-buttons :min="0" :max="59" :disabled="props.dateSelectionLocked" />
                            </div>
                            <div class="flex-row-with-gap">
                                <label>End</label>
                                <input-number class="time-input-number-width" v-model="endHour" show-buttons :min="0" :max="23" :disabled="props.dateSelectionLocked" />
                                <span>:</span>
                                <input-number class="time-input-number-width" v-model="endMinute" show-buttons :min="0" :max="59" :disabled="props.dateSelectionLocked" />
                            </div>
                        </div>
                        <div v-if="hourRangeMode === 'relative'" class="time-input-row">
                            <div class="flex-row-with-gap">
                                <label>Hours back</label>
                                <input-number class="input-number-width" v-model="hoursBefore" show-buttons :min="0" :disabled="props.dateSelectionLocked" />
                            </div>
                            <div class="flex-row-with-gap">
                                <label>Hours forward</label>
                                <input-number class="input-number-width" v-model="hoursAfter" show-buttons :min="0" :disabled="props.dateSelectionLocked" />
                            </div>
                        </div>
                    </div>
                </accordion-content>
            </accordion-panel>
        </accordion>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionContent from 'primevue/accordioncontent';
import AccordionHeader from 'primevue/accordionheader';
import InputNumber from 'primevue/inputnumber';
import PrimevueSelect from 'primevue/select';
import SelectButton from 'primevue/selectbutton';
import dayjs, { Dayjs } from 'dayjs';
import { DATE_RANGE_BY_ID } from '@/constants/DateRanges.ts';
import { DateFormats } from '@/constants/DateFormats.ts';
import { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { IDateRangeByString, IDateRangeByStringWithType } from '@/interfaces/general/IDateRangeByString.ts';
import { getDatesFromConfig } from '@/helpers/datePickerMethod.ts';
import { TDatePickerMinimumView } from '@/types/powerMarkets/TDatePickerMinimumView.ts';
import {
    dateFormatToAnyFormat,
    getLastYearDayOffset,
    resolveDateRange,
} from '@/utils/dateUtils.ts';
import InputWithLabel from '@/components/common/InputWithLabel.vue';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import RelativeDateRangeSlider from '@/components/powerMarkets/date-picker/RelativeDateRangeSlider.vue';
import BasicDatePicker from '@/components/powerMarkets/common/BasicDatePicker.vue';

const DATE_TYPE_FIXED = 'fixed';
const DATE_TYPE_VARIABLE = 'variable';

type QuickRangeId = 'today' | 'yesterday' | 'tomorrow' | 'last7Days' | 'currentWeek' | 'currentMonth' | 'currentYear' | 'yearToDate' | 'last12Months';
type DatePickerTab = 'preset' | 'relative' | 'dates';
type HourRangeMode = 'none' | 'fixed' | 'relative';
type DateSelection = Date | [Date, Date];
type DatePickerPayload = Pick<IAdvancedDatePickerModel,
'dateRange'
| 'dateType'
| 'rangeSelectType'
| 'isHourRangeEnabled'
| 'hoursBefore'
| 'hoursAfter'
| 'daysBefore'
| 'daysAfter'
| 'variableDateStartString'
| 'variableDateEndString'
>;

interface IHourRangeModeOption extends IIdName {
    id: HourRangeMode;
    disabled?: boolean;
    tooltip?: string;
}

const SINGLE_DAY_QUICK_RANGE_IDS = ['today', 'yesterday', 'tomorrow'];
const MULTI_DAY_ONLY_QUICK_RANGE_IDS = ['last7Days', 'currentWeek', 'currentMonth', 'yearToDate', 'last12Months', 'currentYear'];

const VISIBLE_QUICK_RANGE_OPTIONS: Array<IIdName> = [{
    name: 'Today',
    id: 'today',
}, {
    name: 'Yesterday',
    id: 'yesterday',
}, {
    name: 'Tomorrow',
    id: 'tomorrow',
}, {
    name: 'Last 7 days',
    id: 'last7Days',
}, {
    name: 'This week',
    id: 'currentWeek',
}, {
    name: 'This month',
    id: 'currentMonth',
}, {
    name: 'YTD',
    id: 'yearToDate',
}, {
    name: 'Last 12 months',
    id: 'last12Months',
}, {
    name: 'Full current year',
    id: 'currentYear',
}];

const DATE_PICKER_TAB_OPTIONS: Array<IIdName> = [{
    name: 'Quick select',
    id: 'preset',
}, {
    name: 'Fixed',
    id: 'dates',
}, {
    name: 'Relative',
    id: 'relative',
    info: 'Always relative to today, so the selected range updates as time passes.',
}];

const DEFAULT_ROLLING_OFFSET_RANGE: [number, number] = [-7, 14];
const DEFAULT_ROLLING_SLIDER_MIN = -28;
const DEFAULT_ROLLING_SLIDER_MAX = 28;
const ROLLING_SLIDER_PADDING = 7; // buffer used if limits of slider are expanded based on user input

const QUICK_SELECT_TO_TYPED_DATE_RANGE: Record<string, IDateRangeByStringWithType> = {
    today: {
        from: { type: 'date', value: '0' },
        to: { type: 'date', value: '0' },
    },
    yesterday: {
        from: { type: 'dynamic', value: '1' },
        to: { type: 'dynamic', value: '-1' },
    },
    tomorrow: {
        from: { type: 'dynamic', value: '-1' },
        to: { type: 'dynamic', value: '1' },
    },
    last7Days: {
        from: { type: 'dynamic', value: '6' },
        to: { type: 'dynamic', value: '0' },
    },
    currentWeek: {
        from: { type: 'dynamic', value: 'StartWeek' },
        to: { type: 'dynamic', value: 'EndWeek' },
    },
    currentMonth: {
        from: { type: 'dynamic', value: 'StartMonth' },
        to: { type: 'dynamic', value: 'EndMonth' },
    },
    currentYear: {
        from: { type: 'dynamic', value: 'StartYear' },
        to: { type: 'dynamic', value: 'EndYear' },
    },
    yearToDate: {
        from: { type: 'dynamic', value: 'StartYear' },
        to: { type: 'dynamic', value: '0' },
    },
    last12Months: {
        from: { type: 'dynamic', value: 'Last12Months' },
        to: { type: 'dynamic', value: '0' },
    },
};

function getTypedDateRangeForQuickSelect (quickRangeId: string): IDateRangeByStringWithType | undefined {
    return QUICK_SELECT_TO_TYPED_DATE_RANGE[quickRangeId];
}

const VARIABLE_DATE_START_STRINGS: Array<NonNullable<IAdvancedDatePickerModel['variableDateStartString']>> = [
    'StartWeek',
    'StartMonth',
    'StartYear',
    'Last12Months',
];

function isVariableDateStartString (value: string): value is NonNullable<IAdvancedDatePickerModel['variableDateStartString']> {
    return VARIABLE_DATE_START_STRINGS.includes(value as NonNullable<IAdvancedDatePickerModel['variableDateStartString']>);
}

const VARIABLE_DATE_MARKERS_TO_QUICK_SELECT_ID: Record<string, string> = {
    'StartWeek|EndWeek': 'currentWeek',
    'StartMonth|EndMonth': 'currentMonth',
    'StartYear|EndYear': 'currentYear',
    'StartYear|undefined': 'yearToDate',
    'Last12Months|undefined': 'last12Months',
};

const START_OF_DAY = new Date(new Date().setHours(0, 0, 0, 0));
const END_OF_DAY = new Date(new Date().setHours(23, 30, 0, 0));

const props = defineProps<{
    modelValue: IAdvancedDatePickerModel | null;
    defaultModelValue?: IAdvancedDatePickerModel | null;
    daysOnly?: boolean;
    dateSelectionLocked?: boolean;
    onlyShowSingleDay?: boolean;
    minimumView?: TDatePickerMinimumView;
    minDate?: Date;
    maxDate?: Date;
}>();

const selectedFromDate = ref<Date>(START_OF_DAY);
const selectedToDate = ref<Date>(END_OF_DAY);
const daysBefore = ref<number>(0);
const daysAfter = ref<number>(1);
const startHour = ref<number>(0);
const startMinute = ref<number>(0);
const endHour = ref<number>(23);
const endMinute = ref<number>(59);
const hoursBefore = ref<number>(1);
const hoursAfter = ref<number>(1);

const quickSelectDateRangeId = ref<QuickRangeId>('today');

const currentDateType = ref<'fixed' | 'variable'>(DATE_TYPE_VARIABLE);
const hourRangeMode = ref<HourRangeMode>('none');
const advancedTimeOpen = ref<boolean>(false);
const dateMode = ref<'single' | 'range'>('single');

const lastPresetQuickRangeId = ref<QuickRangeId>('today');
const activeDatePickerTab = ref<DatePickerTab>('preset');

const visibleQuickRangeOptions = computed<Array<IIdName>>(() => {
    const options = props.onlyShowSingleDay
        ? VISIBLE_QUICK_RANGE_OPTIONS.filter(option => !MULTI_DAY_ONLY_QUICK_RANGE_IDS.includes(option.id))
        : VISIBLE_QUICK_RANGE_OPTIONS;

    return options.filter(option => isQuickRangeWithinDateLimits(option.id));
});

const quickRangeSelectOptions = computed<Array<IIdName>>(() => [...visibleQuickRangeOptions.value]);

const canUsePresetMode = computed<boolean>(() => visibleQuickRangeOptions.value.length > 0);

const datePickerTabOptions = computed<Array<IIdName & { disabled?: boolean }>>(() => DATE_PICKER_TAB_OPTIONS.map(option => ({
    ...option,
    disabled: option.id === 'preset' && !canUsePresetMode.value,
})));

const advancedTimePanelValue = computed<string | null>({
    get: () => (advancedTimeOpen.value ? 'advanced-time' : null),
    set: (value) => {
        advancedTimeOpen.value = value === 'advanced-time';
    },
});

const showFixedDateRange = computed<boolean>(() => dateMode.value === 'range' && !props.onlyShowSingleDay);

const rollingStartOffset = computed<number>(() => -daysBefore.value);
const rollingEndOffset = computed<number>(() => daysAfter.value);

const rollingOffsetRange = computed<[number, number]>({
    get: (): [number, number] => [rollingStartOffset.value, rollingEndOffset.value],
    set: updateRollingOffsetRange,
});

const defaultRollingOffsetRange = computed<[number, number]>(() => clampRollingOffsetRange(getRollingOffsetRangeFromModel(props.defaultModelValue) ?? DEFAULT_ROLLING_OFFSET_RANGE));

const rollingSliderMin = computed<number>(() => getRollingSliderBoundary('min'));
const rollingSliderMax = computed<number>(() => getRollingSliderBoundary('max'));
const rollingMinOffsetLimit = computed<number | null>(() => getRollingLimitOffset(props.minDate));
const rollingMaxOffsetLimit = computed<number | null>(() => getRollingLimitOffset(props.maxDate));
const rollingInputMin = computed<number | undefined>(() => rollingMinOffsetLimit.value ?? undefined);
const rollingInputMax = computed<number | undefined>(() => rollingMaxOffsetLimit.value ?? undefined);

const rollingStartSignedOffset = computed<number>({
    get: () => rollingStartOffset.value,
    set: value => updateRollingStartOffset(value ?? 0),
});

const rollingEndSignedOffset = computed<number>({
    get: () => rollingEndOffset.value,
    set: value => updateRollingEndOffset(value ?? 0),
});

const isSelectedDayToday = computed<boolean>(() => {
    if (effectiveDateType.value !== DATE_TYPE_VARIABLE) return false;
    if (activeDatePickerTab.value === 'preset' && quickSelectDateRangeId.value === 'today') return true;
    if (activeDatePickerTab.value === 'relative' && daysBefore.value === 0 && daysAfter.value === 0) return true;
    return false;
});

const canUseRollingHours = computed<boolean>(() => isSelectedDayToday.value);
const shouldShowRollingHours = computed<boolean>(() => activeDatePickerTab.value !== 'dates');

const hourRangeModeOptions = computed<Array<IHourRangeModeOption>>(() => {
    const rollingHoursOption: IHourRangeModeOption = {
        name: 'Rolling hours',
        id: 'relative',
        disabled: !canUseRollingHours.value,
        tooltip: canUseRollingHours.value ? '' : 'Rolling hours are only available for today.',
    };
    const options: Array<IHourRangeModeOption> = [
        { name: 'Full day', id: 'none' },
        { name: 'Specific hours', id: 'fixed' },
        ...(shouldShowRollingHours.value ? [rollingHoursOption] : []),
    ];

    return options;
});

const effectiveDateType = computed<'fixed' | 'variable'>(() => {
    if (activeDatePickerTab.value === 'dates') return DATE_TYPE_FIXED;
    return DATE_TYPE_VARIABLE;
});

const fixedDateMinimumView = computed<TDatePickerMinimumView>(() => (
    effectiveDateType.value === DATE_TYPE_FIXED ? props.minimumView ?? 'day' : 'day'
));

const fixedDatePickerView = computed<'date' | 'month'>(() => (
    fixedDateMinimumView.value === 'month' ? 'month' : 'date'
));

const fixedDatePickerFormat = computed<string>(() => (
    fixedDateMinimumView.value === 'month' ? DateFormats.MONTH : DateFormats.SHORT_SIMPLE
));

const fixedDateLabel = computed<string>(() => (fixedDateMinimumView.value === 'month' ? 'Month' : 'Date'));

const fixedDateModeOptions = computed<Array<IIdName>>(() => [
    { id: 'single', name: fixedDateMinimumView.value === 'month' ? 'Single month' : 'Single day' },
    { id: 'range', name: fixedDateMinimumView.value === 'month' ? 'Month range' : 'Date range' },
]);

const showHourRangeOptions = computed<boolean>(() => (
    !props.daysOnly && fixedDateMinimumView.value === 'day' && props.minimumView !== 'month' && activeDatePickerTab.value !== 'preset'
));

function updateActiveDatePickerTab (tab: string | number): void {
    const nextTab = tab as DatePickerTab;

    if (props.dateSelectionLocked) return;
    if (nextTab === activeDatePickerTab.value) return;
    if (nextTab === 'preset' && !canUsePresetMode.value) return;

    activeDatePickerTab.value = nextTab;

    if (nextTab === 'dates') {
        currentDateType.value = DATE_TYPE_FIXED;
        setDateMode(selectedFromDate.value, selectedToDate.value);
        return;
    }

    if (nextTab === 'relative') {
        currentDateType.value = DATE_TYPE_VARIABLE;
        const typedRange = getTypedDateRangeForQuickSelect(quickSelectDateRangeId.value);
        const quickSelectStartOffset = Number(typedRange?.from.value);
        const quickSelectEndOffset = Number(typedRange?.to.value);

        if (Number.isFinite(quickSelectStartOffset) && Number.isFinite(quickSelectEndOffset)) {
            updateRollingOffsetRange([-quickSelectStartOffset, quickSelectEndOffset]);
        } else {
            setRollingOffsetsFromSelectedDates();
        }
        clampCurrentRollingOffsetsToLimits();
        return;
    }

    selectQuickRange(getNextPresetQuickRangeId());
}

function selectQuickRange (quickRangeId: string): void {
    if (props.dateSelectionLocked) return;
    if (!isQuickRangeWithinDateLimits(quickRangeId)) return;
    quickSelectDateRangeId.value = quickRangeId as QuickRangeId;
    lastPresetQuickRangeId.value = quickSelectDateRangeId.value;
    activeDatePickerTab.value = 'preset';
    updateDateRange();
}

function updateFixedDateMode (mode: 'single' | 'range'): void {
    if (props.dateSelectionLocked) return;
    dateMode.value = mode;
    if (mode === 'single') selectedToDate.value = selectedFromDate.value;
}

function updateSpecificDateSelection (value: DateSelection, boundary: 'from' | 'to'): void {
    if (props.dateSelectionLocked) return;
    currentDateType.value = DATE_TYPE_FIXED;
    const date = normaliseFixedPickerDate(Array.isArray(value) ? value[0] : value);

    if (boundary === 'from') {
        selectedFromDate.value = date;
        if (!showFixedDateRange.value || dayjs(date).isAfter(selectedToDate.value)) selectedToDate.value = date;
    } else {
        selectedToDate.value = date;
        if (dayjs(date).isBefore(selectedFromDate.value)) selectedFromDate.value = date;
    }
}

function normaliseFixedPickerDate (date: Date): Date {
    if (fixedDateMinimumView.value === 'month') {
        return dayjs(date).startOf('month').toDate();
    }

    return date;
}

function getFixedPayloadBoundary (date: Date, boundary: 'from' | 'to'): Dayjs {
    const parsedDate = dayjs(date);

    if (fixedDateMinimumView.value === 'month') {
        return boundary === 'from' ? parsedDate.startOf('month') : parsedDate.endOf('month');
    }

    return boundary === 'from' ? parsedDate.startOf('day') : parsedDate.endOf('day');
}

function getQuickRangeDayBounds (quickRangeId: string): { from: Dayjs; to: Dayjs } | null {
    const typedRange = getTypedDateRangeForQuickSelect(quickRangeId);
    if (!typedRange) return null;

    const { from, to } = resolveDateRange(typedRange.from, typedRange.to);
    if (!from?.isValid() || !to?.isValid()) return null;

    return {
        from: from.startOf('day'),
        to: to.endOf('day'),
    };
}

function isQuickRangeWithinDateLimits (quickRangeId: string): boolean {
    const bounds = getQuickRangeDayBounds(quickRangeId);
    if (!bounds) return false;

    const minDate = props.minDate ? dayjs(props.minDate).startOf('day') : null;
    const maxDate = props.maxDate ? dayjs(props.maxDate).endOf('day') : null;

    if (minDate?.isValid() && bounds.from.isBefore(minDate)) return false;
    if (maxDate?.isValid() && bounds.to.isAfter(maxDate)) return false;

    return true;
}

function getQuickRangeDateMode (quickRangeId: QuickRangeId): 'single' | 'range' {
    return SINGLE_DAY_QUICK_RANGE_IDS.includes(quickRangeId) ? 'single' : 'range';
}

function getNextPresetQuickRangeId (): QuickRangeId {
    const visibleIds = visibleQuickRangeOptions.value.map(option => option.id);

    if (visibleIds.includes(lastPresetQuickRangeId.value)) {
        return lastPresetQuickRangeId.value;
    }

    return (visibleQuickRangeOptions.value[0]?.id as QuickRangeId) ?? 'today';
}

function ensureSelectedQuickRangeWithinDateLimits (): void {
    if (activeDatePickerTab.value !== 'preset') return;
    if (isQuickRangeWithinDateLimits(quickSelectDateRangeId.value)) return;

    const fallbackQuickRangeId = visibleQuickRangeOptions.value[0]?.id as QuickRangeId | undefined;
    if (!fallbackQuickRangeId) {
        activeDatePickerTab.value = 'relative';
        currentDateType.value = DATE_TYPE_VARIABLE;
        setRollingOffsetsFromSelectedDates();
        clampCurrentRollingOffsetsToLimits();
        return;
    }

    quickSelectDateRangeId.value = fallbackQuickRangeId;
    lastPresetQuickRangeId.value = fallbackQuickRangeId;
    updateDateRange();
}

function getOrderedRollingRange (startOffset: number, endOffset: number): [number, number] {
    return startOffset <= endOffset ? [startOffset, endOffset] : [endOffset, startOffset];
}

function getRollingLimitOffset (date: Date | undefined): number | null {
    if (!date) return null;
    const parsedDate = dayjs(date);
    if (!parsedDate.isValid()) return null;

    return parsedDate.startOf('day').diff(dayjs().startOf('day'), 'day');
}

function clampRollingOffset (offset: number): number {
    let clamped = offset;
    if (rollingMinOffsetLimit.value !== null) {
        clamped = Math.max(clamped, rollingMinOffsetLimit.value);
    }
    if (rollingMaxOffsetLimit.value !== null) {
        clamped = Math.min(clamped, rollingMaxOffsetLimit.value);
    }

    return clamped;
}

function clampRollingOffsetRange (range: [number, number]): [number, number] {
    const [start, end] = getOrderedRollingRange(range[0], range[1]);
    return getOrderedRollingRange(clampRollingOffset(start), clampRollingOffset(end));
}

function clampCurrentRollingOffsetsToLimits (): void {
    if (currentDateType.value !== DATE_TYPE_VARIABLE || activeDatePickerTab.value !== 'relative' || hourRangeMode.value === 'relative') return;

    updateRollingOffsetRange(rollingOffsetRange.value);
}

function roundDownToSliderStep (value: number): number {
    return Math.floor(value / ROLLING_SLIDER_PADDING) * ROLLING_SLIDER_PADDING;
}

function roundUpToSliderStep (value: number): number {
    return Math.ceil(value / ROLLING_SLIDER_PADDING) * ROLLING_SLIDER_PADDING;
}

function getRollingSliderBoundary (boundary: 'min' | 'max'): number {
    const offsets = [
        ...rollingOffsetRange.value,
        ...defaultRollingOffsetRange.value,
    ];
    const minOffset = Math.min(...offsets);
    const maxOffset = Math.max(...offsets);

    if (boundary === 'min') {
        const unboundedMin = minOffset < DEFAULT_ROLLING_SLIDER_MIN
            ? roundDownToSliderStep(minOffset - ROLLING_SLIDER_PADDING)
            : DEFAULT_ROLLING_SLIDER_MIN;
        return rollingMinOffsetLimit.value === null ? unboundedMin : Math.max(unboundedMin, rollingMinOffsetLimit.value);
    }

    const unboundedMax = maxOffset > DEFAULT_ROLLING_SLIDER_MAX
        ? roundUpToSliderStep(maxOffset + ROLLING_SLIDER_PADDING)
        : DEFAULT_ROLLING_SLIDER_MAX;
    return rollingMaxOffsetLimit.value === null ? unboundedMax : Math.min(unboundedMax, rollingMaxOffsetLimit.value);
}

function getRollingOffsetRangeFromModel (model: IAdvancedDatePickerModel | null | undefined): [number, number] | null {
    if (!model || model.dateType === DATE_TYPE_FIXED || model.rangeSelectType === 'hours') return null;

    if (typeof model.daysBefore === 'number' && typeof model.daysAfter === 'number') {
        return getOrderedRollingRange(-model.daysBefore, model.daysAfter);
    }

    if (isTypedDateRange(model.dateRange)) {
        const fromValue = Number(model.dateRange.from.value);
        const toValue = Number(model.dateRange.to.value);
        if (Number.isFinite(fromValue) && Number.isFinite(toValue)) {
            return getOrderedRollingRange(-fromValue, toValue);
        }
    } else if ((model.dateType === DATE_TYPE_VARIABLE || model.rangeSelectType === 'days') && typeof model.dateRange.from === 'string' && typeof model.dateRange.to === 'string') {
        const today = dayjs().startOf('day');
        const from = dayjs(model.dateRange.from);
        const to = dayjs(model.dateRange.to);

        if (from.isValid() && to.isValid()) {
            return getOrderedRollingRange(from.startOf('day').diff(today, 'day'), to.startOf('day').diff(today, 'day'));
        }
    }

    return null;
}

function updateRollingOffsetRange (range: [number, number]): void {
    const [start, end] = clampRollingOffsetRange(range);
    currentDateType.value = DATE_TYPE_VARIABLE;
    activeDatePickerTab.value = 'relative';
    daysBefore.value = -start;
    daysAfter.value = end;
    dateMode.value = start === end ? 'single' : 'range';
}

function setRollingOffsetsFromSelectedDates (): void {
    const today = dayjs().startOf('day');
    const start = dayjs(selectedFromDate.value).startOf('day').diff(today, 'day');
    const end = dayjs(selectedToDate.value).startOf('day').diff(today, 'day');

    updateRollingOffsetRange(getOrderedRollingRange(start, end));
}

function updateRollingStartOffset (offset: number): void {
    updateRollingOffsetRange([offset, Math.max(offset, rollingEndOffset.value)]);
}

function updateRollingEndOffset (offset: number): void {
    updateRollingOffsetRange([Math.min(rollingStartOffset.value, offset), offset]);
}

function clearSelection (): void {
    selectedFromDate.value = new Date(START_OF_DAY);
    selectedToDate.value = new Date(END_OF_DAY);
}

function updateDateRange (): void {
    if (props.dateSelectionLocked) return;
    if (!quickSelectDateRangeId.value) return;

    if (activeDatePickerTab.value !== 'preset') return;

    currentDateType.value = DATE_TYPE_VARIABLE;
    dateMode.value = getQuickRangeDateMode(quickSelectDateRangeId.value);

    hourRangeMode.value = 'none';

    const typedRange = getTypedDateRangeForQuickSelect(quickSelectDateRangeId.value);
    if (typedRange) {
        const { from, to } = resolveDateRange(typedRange.from, typedRange.to);
        selectedFromDate.value = from?.toDate() ?? selectedFromDate.value;
        selectedToDate.value = to?.toDate() ?? selectedToDate.value;
    }

    const range = DATE_RANGE_BY_ID[quickSelectDateRangeId.value];
    if (!range) return;
    [selectedFromDate.value, selectedToDate.value] = range;
}

function createDateRangeForPayload (): IDateRangeByString | IDateRangeByStringWithType {
    if (effectiveDateType.value === DATE_TYPE_VARIABLE && activeDatePickerTab.value === 'preset' && hourRangeMode.value !== 'relative') {
        const typedRange = getTypedDateRangeForQuickSelect(quickSelectDateRangeId.value);
        if (typedRange) {
            const result: IDateRangeByStringWithType = {
                from: { ...typedRange.from },
                to: { ...typedRange.to },
            };
            if (hourRangeMode.value === 'fixed') {
                result.fromHoursForDay = dayjs().hour(startHour.value).minute(startMinute.value).format('HH:mm');
                result.toHoursForDay = dayjs().hour(endHour.value).minute(endMinute.value).format('HH:mm');
            }
            return result;
        }
    }

    const effectiveToDate = (effectiveDateType.value === DATE_TYPE_FIXED && (dateMode.value === 'single' || props.onlyShowSingleDay))
        ? selectedFromDate.value
        : selectedToDate.value;

    let from = dayjs();
    let to = dayjs();

    if (effectiveDateType.value === DATE_TYPE_FIXED) {
        from = getFixedPayloadBoundary(selectedFromDate.value, 'from');
        to = getFixedPayloadBoundary(effectiveToDate, 'to');

        if (hourRangeMode.value === 'fixed') {
            from = from.set('hour', startHour.value).set('minute', startMinute.value).set('second', 0).set('millisecond', 0);
            to = to.set('hour', endHour.value).set('minute', endMinute.value).set('second', 0).set('millisecond', 0);
        }
    } else if (effectiveDateType.value === DATE_TYPE_VARIABLE) {
        if (hourRangeMode.value === 'relative') {
            from = from.subtract(hoursBefore.value, 'hour');
            to = to.add(hoursAfter.value, 'hour');
        } else if (activeDatePickerTab.value === 'preset') {
            from = dayjs(DATE_RANGE_BY_ID[quickSelectDateRangeId.value][0]);
            to = dayjs(DATE_RANGE_BY_ID[quickSelectDateRangeId.value][1]);
        } else {
            from = from.subtract(daysBefore.value, 'day').startOf('day');
            if (dateMode.value === 'single') {
                to = from.endOf('day');
            } else {
                to = to.add(daysAfter.value, 'day').endOf('day');
            }
        }
        if (hourRangeMode.value === 'fixed') {
            from = from.set('hour', startHour.value).set('minute', startMinute.value).set('second', 0).set('millisecond', 0);
            to = to.set('hour', endHour.value).set('minute', endMinute.value).set('second', 0).set('millisecond', 0);
        }
    }

    const result: IDateRangeByString = {
        from: from.format('YYYY-MM-DDTHH:mm:ss.SSS'),
        to: to.format('YYYY-MM-DDTHH:mm:ss.SSS'),
        fromTimeInUnix: from.unix(),
        toTimeInUnix: to.unix(),
    };

    if (effectiveDateType.value === DATE_TYPE_VARIABLE && hourRangeMode.value === 'fixed') {
        result.fromHoursForDay = dayjs().hour(startHour.value).minute(startMinute.value).format('HH:mm');
        result.toHoursForDay = dayjs().hour(endHour.value).minute(endMinute.value).format('HH:mm');
    }

    return result;
}

function setQuickSelectFromVariableDateMarkers (): void {
    const startMarker = props.modelValue?.variableDateStartString;
    const endMarker = props.modelValue?.variableDateEndString;
    const markerKey = `${startMarker}|${endMarker}`;
    const markerPreset = VARIABLE_DATE_MARKERS_TO_QUICK_SELECT_ID[markerKey];

    if (markerPreset) {
        quickSelectDateRangeId.value = markerPreset as QuickRangeId;
        lastPresetQuickRangeId.value = quickSelectDateRangeId.value;

        if (['currentWeek', 'currentMonth', 'currentYear', 'yearToDate', 'last12Months'].includes(quickSelectDateRangeId.value)) {
            dateMode.value = 'range';
        }
        return;
    }

    if (daysBefore.value === 6 && daysAfter.value === 0 && hourRangeMode.value === 'none') {
        dateMode.value = 'range';
        quickSelectDateRangeId.value = 'last7Days';
        lastPresetQuickRangeId.value = quickSelectDateRangeId.value;
        return;
    }

    if (daysBefore.value === getLastYearDayOffset() && daysAfter.value === 0 && hourRangeMode.value === 'none') {
        dateMode.value = 'range';
        quickSelectDateRangeId.value = 'last12Months';
        lastPresetQuickRangeId.value = quickSelectDateRangeId.value;
        return;
    }

    if (daysAfter.value === -daysBefore.value) {
        dateMode.value = 'single';
        switch (daysBefore.value) {
            case 1:
                if (hourRangeMode.value === 'none') {
                    quickSelectDateRangeId.value = 'yesterday';
                    lastPresetQuickRangeId.value = quickSelectDateRangeId.value;
                    return;
                }
                break;
            case -1:
                if (hourRangeMode.value === 'none') {
                    quickSelectDateRangeId.value = 'tomorrow';
                    lastPresetQuickRangeId.value = quickSelectDateRangeId.value;
                    return;
                }
                break;
            case 0:
                if (hourRangeMode.value === 'none') {
                    quickSelectDateRangeId.value = 'today';
                    lastPresetQuickRangeId.value = quickSelectDateRangeId.value;
                    return;
                }
                break;
        }
    }

    activeDatePickerTab.value = 'relative';
    if (daysAfter.value !== -daysBefore.value) {
        dateMode.value = 'range';
    }
}

function getPayload (): DatePickerPayload {
    const dateRange = createDateRangeForPayload();

    const payload: DatePickerPayload = {
        dateRange,
        dateType: effectiveDateType.value,
        rangeSelectType: hourRangeMode.value === 'relative' ? 'hours' : 'days',
        isHourRangeEnabled: hourRangeMode.value === 'fixed',
        hoursBefore: null,
        hoursAfter: null,
        daysBefore: null,
        daysAfter: null,
    };

    if (effectiveDateType.value === DATE_TYPE_VARIABLE) {
        const typedDateRangeForQuickSelect = activeDatePickerTab.value === 'preset'
            ? getTypedDateRangeForQuickSelect(quickSelectDateRangeId.value)
            : undefined;
        const typedFromValue = typedDateRangeForQuickSelect?.from.value;
        const typedToValue = typedDateRangeForQuickSelect?.to.value;
        payload.variableDateStartString = typeof typedFromValue === 'string' && isVariableDateStartString(typedFromValue)
            ? typedFromValue
            : undefined;
        payload.variableDateEndString = typeof typedToValue === 'string' && typedToValue.startsWith('End')
            ? typedToValue as IAdvancedDatePickerModel['variableDateEndString']
            : undefined;

        if (!isTypedDateRange(payload.dateRange)) {
            if (hourRangeMode.value === 'relative' && dateMode.value === 'single') {
                payload.hoursBefore = hoursBefore.value;
                payload.hoursAfter = hoursAfter.value;
            } else {
                payload.daysBefore = daysBefore.value;
                payload.daysAfter = dateMode.value === 'range' ? daysAfter.value : -daysBefore.value;
            }
        }
    }

    return payload;
}

function setDateRangeToToday (): void {
    selectedFromDate.value = new Date(START_OF_DAY);
    selectedToDate.value = new Date(END_OF_DAY);
}

function isTypedDateRange (dateRange: IDateRangeByString | IDateRangeByStringWithType): dateRange is IDateRangeByStringWithType {
    return typeof dateRange.from === 'object' && dateRange.from !== null
        && typeof dateRange.to === 'object' && dateRange.to !== null
        && 'type' in dateRange.from && 'value' in dateRange.from
        && 'type' in dateRange.to && 'value' in dateRange.to;
}

function setDateMode (fromDate: Date, toDate: Date): void {
    if (props.onlyShowSingleDay) {
        dateMode.value = 'single';
    } else {
        const fromDay = dayjs(fromDate);
        const toDay = dayjs(toDate);
        dateMode.value = fromDay.isSame(toDay, fixedDateMinimumView.value === 'month' ? 'month' : 'day') ? 'single' : 'range';
    }
}

function setHourRange (fromDate: Date, toDate: Date): void {
    const fromDayjs = dayjs(fromDate);
    const toDayjs = dayjs(toDate);
    const startHourFromRange = fromDayjs.hour();
    const startMinuteFromRange = fromDayjs.minute();
    const endHourFromRange = toDayjs.hour();
    const endMinuteFromRange = toDayjs.minute();

    if (startHourFromRange === 0 && startMinuteFromRange === 0 && endHourFromRange === 23 && endMinuteFromRange >= 59) return;

    hourRangeMode.value = 'fixed';
    startHour.value = startHourFromRange;
    startMinute.value = startMinuteFromRange;
    endHour.value = endHourFromRange;
    endMinute.value = endMinuteFromRange;
}

function disableHourRangeWhenUnsupported (): void {
    if (props.daysOnly || fixedDateMinimumView.value === 'month' || props.minimumView === 'month') {
        hourRangeMode.value = 'none';
    }
}

function setSelectedDate (): void {
    currentDateType.value = props.modelValue?.dateType ?? DATE_TYPE_VARIABLE;

    if (!props.modelValue?.dateRange) {
        setDateRangeToToday();
        return;
    }

    if (isTypedDateRange(props.modelValue.dateRange)) {
        const typedDateRange = props.modelValue.dateRange as IDateRangeByStringWithType;

        const { from: resolvedFrom, to: resolvedTo } = getDatesFromConfig(dateFormatToAnyFormat(typedDateRange, 'IDateObject'));

        selectedFromDate.value = resolvedFrom?.toDate() ?? new Date(START_OF_DAY);
        selectedToDate.value = resolvedTo?.toDate() ?? new Date(END_OF_DAY);

        const fromType = typedDateRange.from.type;
        const toType = typedDateRange.to.type;

        if (props.modelValue.dateType === DATE_TYPE_VARIABLE) {
            if (fromType === 'dynamicHours' || toType === 'dynamicHours' || typedDateRange.recentHour) {
                hourRangeMode.value = 'relative';
                hoursBefore.value = typedDateRange.fromHours ?? props.modelValue.hoursBefore ?? 1;
                hoursAfter.value = typedDateRange.toHours ?? props.modelValue.hoursAfter ?? 1;
                dateMode.value = 'single';
                activeDatePickerTab.value = 'relative';
                daysBefore.value = 0;
                daysAfter.value = 0;
            } else {
                daysBefore.value = Number(typedDateRange.from.value);
                daysAfter.value = Number(typedDateRange.to.value);

                if (Number.isNaN(daysBefore.value)) {
                    daysBefore.value = props.modelValue.daysBefore ?? 1;
                }
                if (Number.isNaN(daysAfter.value)) {
                    daysAfter.value = props.modelValue.daysAfter ?? 1;
                }
                if (props.modelValue.isHourRangeEnabled) {
                    hourRangeMode.value = 'fixed';
                    if (typedDateRange.fromHoursForDay) {
                        const [h, m] = typedDateRange.fromHoursForDay.split(':').map(Number);
                        startHour.value = h;
                        startMinute.value = m ?? 0;
                    }
                    if (typedDateRange.toHoursForDay) {
                        const [h, m] = typedDateRange.toHoursForDay.split(':').map(Number);
                        endHour.value = h;
                        endMinute.value = m ?? 0;
                    }
                }
                setQuickSelectFromVariableDateMarkers();
            }
            return;
        }

        if (fromType === 'dynamic' || toType === 'dynamic') {
            currentDateType.value = DATE_TYPE_VARIABLE;
            daysBefore.value = Number(typedDateRange.from.value);
            daysAfter.value = Number(typedDateRange.to.value);

            if (Number.isNaN(daysBefore.value)) {
                daysBefore.value = props.modelValue.daysBefore ?? 1;
            }
            if (Number.isNaN(daysAfter.value)) {
                daysAfter.value = props.modelValue.daysAfter ?? 1;
            }
            setQuickSelectFromVariableDateMarkers();
            return;
        }
        if (fromType === 'dynamicHours' || toType === 'dynamicHours' || typedDateRange.recentHour) {
            currentDateType.value = DATE_TYPE_VARIABLE;
            hourRangeMode.value = 'relative';
            hoursBefore.value = typedDateRange.fromHours ?? 1;
            hoursAfter.value = typedDateRange.toHours ?? 1;
            dateMode.value = 'single';
            activeDatePickerTab.value = 'relative';
            daysBefore.value = 0;
            daysAfter.value = 0;
        } else if (resolvedFrom && resolvedTo) {
            currentDateType.value = DATE_TYPE_FIXED;
            activeDatePickerTab.value = 'dates';
            selectedFromDate.value = normaliseFixedPickerDate(resolvedFrom.toDate());
            selectedToDate.value = normaliseFixedPickerDate(resolvedTo.toDate());
            setHourRange(resolvedFrom.toDate(), resolvedTo.toDate());
        }
        setDateMode(
            currentDateType.value === DATE_TYPE_FIXED
                ? normaliseFixedPickerDate(resolvedFrom?.toDate() ?? new Date(START_OF_DAY))
                : (resolvedFrom?.toDate() ?? new Date(START_OF_DAY)),
            currentDateType.value === DATE_TYPE_FIXED
                ? normaliseFixedPickerDate(resolvedTo?.toDate() ?? new Date(END_OF_DAY))
                : (resolvedTo?.toDate() ?? new Date(END_OF_DAY)),
        );
        return;
    }

    const {
        from, to, fromHours, toHours,
    } = props.modelValue.dateRange as IDateRangeByString;

    if (props.modelValue.dateType === DATE_TYPE_VARIABLE) {
        if (props.modelValue.rangeSelectType === 'hours') {
            hourRangeMode.value = 'relative';
            dateMode.value = 'single';
            activeDatePickerTab.value = 'relative';
            daysBefore.value = 0;
            daysAfter.value = 0;
        } else if (props.modelValue.isHourRangeEnabled) {
            const range = props.modelValue.dateRange as IDateRangeByString;
            if (range.fromHoursForDay || range.toHoursForDay) {
                hourRangeMode.value = 'fixed';
                if (range.fromHoursForDay) {
                    const [h, m] = range.fromHoursForDay.split(':').map(Number);
                    startHour.value = h;
                    startMinute.value = m ?? 0;
                }
                if (range.toHoursForDay) {
                    const [h, m] = range.toHoursForDay.split(':').map(Number);
                    endHour.value = h;
                    endMinute.value = m ?? 0;
                }
            }
        }
        hoursBefore.value = props.modelValue.hoursBefore ?? 1;
        hoursAfter.value = props.modelValue.hoursAfter ?? 1;
        if (props.modelValue.rangeSelectType !== 'hours') {
            daysBefore.value = props.modelValue.daysBefore ?? 1;
            daysAfter.value = props.modelValue.daysAfter ?? 1;
            setQuickSelectFromVariableDateMarkers();
        }
        return;
    }

    activeDatePickerTab.value = 'dates';
    selectedFromDate.value = normaliseFixedPickerDate(new Date(from));
    selectedToDate.value = normaliseFixedPickerDate(new Date(to));

    startHour.value = fromHours ?? 0;
    endHour.value = toHours ?? 23;
    hourRangeMode.value = props.modelValue.isHourRangeEnabled ? 'fixed' : 'none';

    setHourRange(new Date(from), new Date(to));

    setDateMode(selectedFromDate.value, selectedToDate.value);
}

function initialise (): void {
    setSelectedDate();
    ensureSelectedQuickRangeWithinDateLimits();
    clampCurrentRollingOffsetsToLimits();
    disableHourRangeWhenUnsupported();
    advancedTimeOpen.value = showHourRangeOptions.value && hourRangeMode.value !== 'none';
}

initialise();

defineExpose({
    getPayload,
    clearSelection,
});

watch(isSelectedDayToday, (isToday) => {
    if (!isToday && hourRangeMode.value === 'relative') {
        hourRangeMode.value = 'none';
    }
});

watch(() => [props.minDate, props.maxDate], () => {
    ensureSelectedQuickRangeWithinDateLimits();
    clampCurrentRollingOffsetsToLimits();
});
</script>

<style lang="scss" scoped>
.input-number-width :deep(.p-inputnumber-input) {
    width: 8rem !important;
}

.signed-day-input :deep(.p-inputnumber-input) {
    width: 8rem !important;
}

.time-input-number-width :deep(.p-inputnumber-input) {
    width: 6rem !important;
}

:deep(.p-accordionheader),
.section-title {
    font-size: var(--extraSmallFontSize);
    text-transform: uppercase;
    color: var(--neutral600);
}

.separator {
    height: 1px;
    background-color: var(--neutral300);
    flex: 1;
}

.separator-wrapper {
    display: flex;
    align-items: center;
    gap: var(--smallSpacing);
}

.column {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);

    label {
        white-space: nowrap;
    }
}

.advanced-time-panel {
    width: 100%;
}

.quick-range-select {
    max-width: 18rem;
    width: 100%;
}

.specific-date-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--defaultSpacing);
}

.specific-date-picker {
    min-width: 0;
}

.rolling-period-panel {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    margin-top: var(--defaultSpacing);
}

.rolling-input-grid {
    display: flex;
    gap: var(--defaultSpacing);
    justify-content: space-between;

    @include responsive('sm') {
        grid-template-columns: 1fr;
    }
}

.rolling-input {
    display: flex;
    align-items: center;
    gap: var(--smallSpacing);
    flex-wrap: wrap;
}

.time-input-row {
    display: flex;
    align-items: center;
    gap: var(--smallSpacing);
    flex-wrap: no-wrap;
    margin-top: var(--defaultSpacing);
}

:deep(.date-picker) {
    width: 100%;
}

:deep(.p-calendar) {
    width: 100%;

    .p-inputtext {
        font-size: var(--defaultFontSize);
    }
}
</style>
